import { useAuthentication } from '@hurdl/authentication/frontend';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { createMenuWorkspaceOperations } from '../bespoke/menu-workspace/menus-operations.js';
import { type MenuWorkspaceOperations } from '../bespoke/menu-workspace/types.js';

/**
 * The one place the fast-food menu operations are wired, and the context the pages read
 * them through (admin-panel R13.6).
 *
 * The review and menus pages are first-class routes now rather than one Bespoke_Page, so
 * each reaches the Menus_Backend through the same composed operations. Rather than let
 * each page compose its own — which would scatter the wiring and make a Fixture unable to
 * substitute a stand-in — the operations are composed once here as a singleton, and a
 * page reads them through {@link useMenuOperations}. The real application wraps the route
 * tree in a {@link MenuOperationsProvider} with no value, which defaults to the
 * singleton; a Fixture wraps the page in one with its own in-memory stand-in, so the
 * appearance check runs with no backend.
 *
 * **No `fetch` and no address literal reaches this module** (admin-panel R13.6): every
 * value below is relative or a plain datum, and the operations' base is the relative
 * mount path the backend serves the review router at — the composition in
 * `menus-operations.ts` assembles it from the Product key, never an absolute URL.
 */

/** The Product the fast-food review and menus routes belong to. */
export const FAST_FOOD_PRODUCT_KEY = 'uk-fast-food-menus';

/** The one Restaurant this build surveys, naming the reviewed-items and category reads. */
export const FAST_FOOD_RESTAURANT_KEY = 'mcdonalds-uk';

/** One Restaurant the menus routes list, by key and the label a reader sees. */
export interface Restaurant {
  readonly key: string;
  readonly label: string;
}

/** The Restaurants the menus routes list. One, in this build. */
export const RESTAURANTS: ReadonlyArray<Restaurant> = [
  { key: FAST_FOOD_RESTAURANT_KEY, label: "McDonald's UK" },
];

/** The same-origin path the panel serves the allergen booklet from — never a literal URL. */
export const ALLERGEN_BOOKLET_URL = '/allergen-booklet-sample.pdf';

/**
 * The operations a page reads, with no default (R9.2).
 *
 * A context rather than a module import so a Fixture can substitute an in-memory stand-in
 * without a backend, and so the real application can supply operations attributed to the
 * **authenticated operator** rather than a hardcoded one. There is no default: the review
 * flow is reached only behind authentication, so a page reading operations is always under
 * a provider — the real {@link SessionMenuOperationsProvider} or a Fixture's — and a page
 * rendered under none is a wiring fault worth surfacing rather than silently reaching a
 * singleton attributed to no one.
 */
const MenuOperationsContext = createContext<MenuWorkspaceOperations | null>(
  null,
);

/** The label a Restaurant key reads as, from {@link RESTAURANTS}, or the key itself. */
export function restaurantLabelOf(restaurantKey: string): string {
  return (
    RESTAURANTS.find((restaurant) => restaurant.key === restaurantKey)?.label ??
    restaurantKey
  );
}

/**
 * Provides a given operations value — the seam a Fixture passes its in-memory stand-in
 * through, so the appearance check runs with no backend.
 */
export function MenuOperationsProvider({
  value,
  children,
}: {
  readonly value: MenuWorkspaceOperations;
  readonly children: ReactNode;
}): ReactElement {
  return createElement(MenuOperationsContext.Provider, { value }, children);
}

/**
 * Provides the real operations, attributed to the authenticated operator (R9.2).
 *
 * The operator is the signed-in user's id, read from the `useAuthentication` snapshot
 * rather than a hardcoded `REVIEW_OPERATOR` — so a review write carries the account that
 * made it, resolved through the composed Authentication_Capability, which is the
 * tightening group 9 records. The operations are composed against the relative mount path
 * the backend serves the review router at, assembled from the Product key in
 * `menus-operations.ts` — a relative path, so no address literal and no `fetch` reaches
 * this module.
 *
 * **The request is the capability's `authorisedFetch`, not the browser's bare `fetch`.**
 * The panel composes the Menus_Backend's review router behind its own
 * Authenticating_Middleware, which admits only a request carrying a verified access token
 * — so a request made through the browser's own `fetch` carries none and is refused 401,
 * even once the operator has signed in. `authorisedFetch` is the store's composed
 * operation: it attaches the access token and refreshes it once on a 401, keeping token
 * handling in the capability rather than this module (invariant 2 — it is a composed
 * operation, not a hand-rolled request or an address literal). Where the dev login bypass
 * is on the middleware is a pass-through, so the token `authorisedFetch` cannot attach
 * (there being no session) is not needed; where it is off, the token is what reaches the
 * live queue.
 *
 * Where the snapshot names no user — a page rendered before the session is established —
 * the operator is the empty string, which the backend's own `resolveActor` (reading the
 * verified claims, not the body) is what actually gates; the body attribution is a
 * provenance the service records, and an unauthenticated request never reaches the service
 * because the Authenticating_Middleware refuses it first.
 */
export function SessionMenuOperationsProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const authentication = useAuthentication();
  const operator = authentication.user?.id ?? '';

  // Wrapped rather than read off the hook result directly: `authorisedFetch` is one of
  // the memoised actions `useAuthentication` returns, and referencing a method of an
  // object as a bare value is what `@typescript-eslint/unbound-method` flags — the hook's
  // action is an arrow with no `this`, so there is no real hazard, but the wrapper is the
  // honest way to say so rather than silencing the rule. The wrapper's identity is stable
  // for as long as the hook result is (the whole returned object is one memo keyed on the
  // snapshot and the store), so the operations memo below rebuilds only when the session
  // actually moves.
  const authorisedFetch = useCallback(
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
      authentication.authorisedFetch(input, init),
    [authentication],
  );

  const operations = useMemo(
    () =>
      createMenuWorkspaceOperations({
        productKey: FAST_FOOD_PRODUCT_KEY,
        pageKey: 'menu-workspace',
        operator,
        authorisedFetch,
      }),
    [operator, authorisedFetch],
  );

  return createElement(
    MenuOperationsContext.Provider,
    { value: operations },
    children,
  );
}

/**
 * The composed Menus_Backend operations the pages read.
 *
 * @throws Error where no provider is above the page — a wiring fault, since the review
 *   flow is reached only behind a provider.
 */
export function useMenuOperations(): MenuWorkspaceOperations {
  const operations = useContext(MenuOperationsContext);

  if (operations === null) {
    throw new Error(
      'useMenuOperations was called with no MenuOperationsProvider above it.',
    );
  }

  return operations;
}
